import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search, Loader2 } from "lucide-react";

/**
 * PositionTrackerTable
 *
 * Props:
 *  - data: array of row objects (for client-side or server-side current page)
 *  - columns: tanstack column definitions
 *  - paginationInfo: optional object for server-side pagination:
 *      { page: 0-based index, pageSize, total, totalPages }
 *  - onPageChange: (pageIndex) => {}  // used for server-side pagination
 *  - onPageSizeChange: (pageSize) => {} // used for server-side pagination
 *  - title: string
 *  - searchable: boolean (show/hide search)
 *  - defaultPageSize: number
 *  - loading: boolean
 */
const debounce = (fn, wait = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

const PositionTrackerTable = ({
  data = [],
  columns = [],
  paginationInfo = null,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  title = "Data Table",
  searchable = true,
  defaultPageSize = 10,
  loading = false,
}) => {
  // Controlled states for table features
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [localPageSize, setLocalPageSize] = useState(paginationInfo?.pageSize || defaultPageSize);
  const [searchTerm, setSearchTerm] = useState("");

  // Debounced setter to improve typing UX
  const debouncedSetGlobal = useMemo(
    () =>
      debounce((v) => {
        setGlobalFilter(v ?? "");
      }, 250),
    []
  );

  // Determine if we are using server-side pagination
  const manualPagination = Boolean(paginationInfo);

  // Create the table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // If server-side, we'll use manualPagination & pageCount
    manualPagination,
    pageCount: manualPagination ? paginationInfo?.totalPages ?? 0 : undefined,
    initialState: {
      pagination: {
        pageIndex: paginationInfo?.page ?? 0,
        pageSize: paginationInfo?.pageSize ?? defaultPageSize,
      },
    },
  });

  // Extract pieces of table state we care about to use in deps
  const tablePageIndex = table.getState().pagination.pageIndex;
  const tablePageSize = table.getState().pagination.pageSize;
  const filteredRowsLength = table.getFilteredRowModel().rows.length;

  // Compute pagination metadata - either use passed paginationInfo (server) or compute from table (client)
  const currentPaginationInfo = useMemo(() => {
    if (paginationInfo) {
      return {
        page: paginationInfo.page,
        pageSize: paginationInfo.pageSize,
        total: paginationInfo.total,
        totalPages: paginationInfo.totalPages,
        hasNextPage: (paginationInfo.page ?? 0) < (paginationInfo.totalPages ?? 0) - 1,
        hasPreviousPage: (paginationInfo.page ?? 0) > 0,
      };
    }

    const paginationState = {
      pageIndex: tablePageIndex ?? 0,
      pageSize: tablePageSize ?? defaultPageSize,
    };

    const totalRows = typeof filteredRowsLength === "number" && filteredRowsLength >= 0 ? filteredRowsLength : data.length;
    const pageSize = paginationState.pageSize || defaultPageSize;
    const totalPages = pageSize > 0 ? Math.ceil(totalRows / pageSize) : 0;
    const pageIndex = paginationState.pageIndex || 0;

    return {
      page: pageIndex,
      pageSize,
      total: totalRows,
      totalPages,
      hasNextPage: pageIndex < totalPages - 1,
      hasPreviousPage: pageIndex > 0,
    };
  }, [paginationInfo, data.length, defaultPageSize, tablePageIndex, tablePageSize, filteredRowsLength]);

  // Keep local page size and table pageIndex synced when paginationInfo changes (server-side updates)
  useEffect(() => {
    if (!paginationInfo) return;
    // sync pageSize
    if (typeof paginationInfo.pageSize === "number" && paginationInfo.pageSize !== localPageSize) {
      setLocalPageSize(paginationInfo.pageSize);
      table.setPageSize(paginationInfo.pageSize);
    }
    // sync page index in the table
    if (typeof paginationInfo.page === "number" && table.getState().pagination.pageIndex !== paginationInfo.page) {
      table.setPageIndex(paginationInfo.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo?.page, paginationInfo?.pageSize, paginationInfo?.total, paginationInfo?.totalPages]);

  // When table pagination changes in client-side mode, update localPageSize and notify parent if needed
  useEffect(() => {
    if (!manualPagination) {
      // keep local state in sync
      if (tablePageSize !== localPageSize) {
        setLocalPageSize(tablePageSize);
        onPageSizeChange?.(tablePageSize);
      }
    } else {
      // In manual (server) mode, if table pageIndex changed (user clicked inside table UI),
      // call onPageChange to let parent fetch new data.
      // Ensure we don't call onPageChange redundantly if parent already updated paginationInfo (handled above).
      // We only call onPageChange when table pageIndex differs from paginationInfo.page (if provided).
      if (typeof paginationInfo?.page === "number") {
        if (tablePageIndex !== paginationInfo.page) {
          onPageChange(tablePageIndex);
        }
      } else {
        // no paginationInfo provided but manualPagination true shouldn't happen,
        // but fallback to notifying parent
        onPageChange(tablePageIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePageIndex, tablePageSize]);

  // UI handlers
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSetGlobal(value);
  }, [debouncedSetGlobal]);

  const handlePageSizeChange = (n) => {
    setLocalPageSize(n);
    table.setPageSize(n);
    if (manualPagination) {
      // Inform parent to fetch new page size (usually you'd reset to page 0)
      onPageSizeChange(n);
      // Often parent will fetch page 0; update table page index to 0 for UI
      table.setPageIndex(0);
    } else {
      onPageSizeChange?.(n);
    }
  };

  const goToPage = (index) => {
    if (manualPagination) {
      onPageChange(index);
      table.setPageIndex(index); // keep UI consistent
    } else {
      table.setPageIndex(index);
    }
  };

  const prevPage = () => {
    const idx = Math.max(0, currentPaginationInfo.page - 1);
    goToPage(idx);
  };

  const nextPage = () => {
    const idx = Math.min(currentPaginationInfo.totalPages - 1, currentPaginationInfo.page + 1);
    goToPage(idx);
  };

  // Generate pagination numbers with proper ellipsis
  const generatePaginationNumbers = () => {
    const currentPage = currentPaginationInfo.page + 1; // Convert to 1-based
    const totalPages = currentPaginationInfo.totalPages;
    const pages = [];
    
    if (totalPages <= 1) return pages;
    
    // Always show first page
    pages.push(1);
    
    // Calculate range around current page
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust range if we're near the beginning or end
    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages - 1);
    }
    if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - 4);
    }
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push('...');
    }
    
    // Add pages in range
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Rows to render (use pagination row model for client-side; when server-side, data already corresponds to page)
  const rowsForRender = table.getRowModel().rows;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            {currentPaginationInfo.total ? `${currentPaginationInfo.total} results found` : `${data.length} results found`}
          </p>
        </div>

        <div className='flex items-center space-x-4'>
          {searchable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 h-4 w-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 text-sm bg-white dark:bg-slate-800 dark:text-slate-100 transition-all duration-200 hover:shadow-md focus:shadow-md"
              />
            </div>
          )}
          
          <div className="relative">
            <select
              value={localPageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="border border-gray-200 dark:border-slate-700 rounded-md px-3 py-2 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 dark:text-slate-100 transition-all duration-200 hover:shadow-md focus:shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Container with Loading Overlay */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white dark:bg-slate-900 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10 rounded-b-xl">
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="text-gray-600 dark:text-slate-400">Loading...</span>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ cursor: header.column.getCanSort() ? "pointer" : "default" }}
                      className="px-6 py-4 text-left whitespace-nowrap"
                      aria-sort={
                        header.column.getIsSorted() ? (header.column.getIsSorted() === "asc" ? "ascending" : "descending") : "none"
                      }
                    >
                      <button className="flex items-center gap-2 text-xs font-medium text-blue-600 dark:text-blue-400 tracking-wider hover:text-blue-700 dark:hover:text-blue-300 transition-colors w-full">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() ? (header.column.getIsSorted() === "asc" ? "▲" : "▼") : null}
                      </button>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
              {rowsForRender.length === 0 ? (
                <tr>
                  <td colSpan={columns.length || 1} className="p-4 text-center text-gray-600 dark:text-slate-400">
                    No Brand On Tracking
                  </td>
                </tr>
              ) : (
                rowsForRender.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-slate-200">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-slate-300">
            Showing <strong>{rowsForRender.length}</strong> of <strong>{currentPaginationInfo.total}</strong> results
          </div>

          <div className="flex items-center gap-1">
            <button 
              onClick={prevPage} 
              disabled={!currentPaginationInfo.hasPreviousPage || loading}
              className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {generatePaginationNumbers().map((pageNum, index) => {
              if (pageNum === '...') {
                return (
                  <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500 dark:text-slate-400">
                    ...
                  </span>
                );
              }
              
              const isCurrentPage = pageNum === currentPaginationInfo.page + 1;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum - 1)}
                  disabled={loading}
                  className={`px-3 py-2 text-sm rounded-md transition-colors ${
                    isCurrentPage
                      ? 'bg-blue-600 text-white font-medium'
                      : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-100'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button 
              onClick={nextPage} 
              disabled={!currentPaginationInfo.hasNextPage || loading}
              className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionTrackerTable;